import React, { useState, useEffect, useRef, useCallback } from "react";
import Grid from "@mui/material/Grid2";

import { 
  Campaign,
  CampaignNPC,
} from "@dndModels";

import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import CampaignNPCInput from "./CampaignNPCInput";
import DandDAPI from "@dndUtilities/system_api";
import LoadingBox from "@components/input/LoadingBox";
import CommonUtilities from "@utilities/common_utilities";
import FlexBox from "@components/layout/FlexBox";
import ModalOrDrawer from "@components/display/ModalOrDrawer";
import DandDButtonBox from "@dndComponents/DandDButtonBoxSimple";

interface Props {
  campaign: Campaign;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function CampaignNPCsInput({
  campaign,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ping, setPing] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [confirm_delete_for, setConfirmDeleteFor] = useState<CampaignNPC | undefined>();
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback( async () => {
    if (campaign.npcs.length === 0) {
      campaign.npcs = await api.getCampaignObjects<CampaignNPC>({ data_type: "campaign_npc", campaign });
    }
    setLoading(false);
  }, [campaign]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 100);
    }
  }, [fetched, load]);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const renderNPCs = () => {
    return (
      <FlexBox>
        <Grid container spacing={0} direction="row">
          { campaign.npcs.map(npc => {
            return (
              <Grid size={4} key={npc._id}>
                <CampaignNPCInput
                  campaign={campaign}
                  npc={npc} 
                  onDelete={() => {
                    setConfirmDeleteFor(npc);
                  }} 
                />
              </Grid>
            );
          })}
        </Grid>
      </FlexBox>
    )
  }

  const renderExtras = () => {
    return (
      <ModalOrDrawer open={confirm_delete_for !== undefined} onClose={() => setConfirmDeleteFor(undefined) }>
        <FlexBox direction="column">
          { confirm_delete_for &&
            <>
              <FlexBox>Are you sure you want to delete {confirm_delete_for.name}? This cannot be undone.</FlexBox>
              <FlexBox>
                <div style={{ float: "right", width: "200px" }}>
                  <FlexBox direction="row">
                    <FlexBox>
                      <DandDButtonBox name="Delete" color="red" onClick={() => {
                        api.deleteObject("campaign_npc", confirm_delete_for);
                        campaign.npcs = campaign.npcs.filter(o => o._id !== confirm_delete_for._id);
                        setConfirmDeleteFor(undefined);
                      }}/>
                    </FlexBox>
                    <FlexBox>
                      <DandDButtonBox name="Cancel" onClick={() => {
                        setConfirmDeleteFor(undefined);
                      }}/>
                    </FlexBox>
                  </FlexBox>
                </div>
              </FlexBox>
            </>
          }
        </FlexBox>
      </ModalOrDrawer>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  }
  return (
    <FlexBox direction="column" style={{ width: "100%" }}>
      <FlexBox className="padded">
        <AddButton label="Add NPC" 
          disabled={processing}
          onClick={ async () => {
            const new_npc = new CampaignNPC();
            new_npc.campaign_id = campaign._id;
            setProcessing(true);
            await api.upsertObject("campaign_npc", new_npc);
            campaign.npcs.push(new_npc);
            setProcessing(false);
          }}
        />
      </FlexBox>
      { renderNPCs() }
      { renderExtras() }
    </FlexBox>
  );
}
